/**
 * The rows behind a figure on the sales report.
 *
 * Every number on that page opens this, and it opens on the rows the
 * number was built from rather than on a rule for finding them again —
 * a sheet that recomputed "no response" from the whole board would open
 * on a hundred rows under a heading that said nine.
 *
 * ## One scope, six tabs
 *
 * A figure names two things: whose leads (a source, an owner, or
 * everybody) and which of them (all, or one outcome). The first is the
 * sheet's scope and the second is a tab, so opening a row and opening
 * one of its cells land in the same place — the cell just arrives with
 * its tab already chosen. Whoever reads the no-shows usually wants the
 * ones being chased next, and should not have to close anything to get
 * there.
 *
 * The cut arrives as a string, because it has to survive being held in
 * one piece of state and read back:
 *
 *   came                 everything in the window
 *   deal | none | …      one outcome, across every source
 *   converted            the ones on the books now
 *   source:Ads           one source, every tab
 *   owner:Swathi:deal    one owner, opened on Moved to deals
 */
import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import { Avatar, EmptyState, Pill } from '../Primitives.jsx';
import { OUTCOMES, OUTCOME_LABEL, lineOf } from '../../domain/salesReport.js';
import { customerOfLead } from '../../domain/crm.js';

/* The tabs, in the order the outcomes are counted, with "everything"
   first and "became clients" last because it cuts across the rest. */
const TABS = [
  ['came', 'Everything'],
  ...OUTCOMES.map(([k, label]) => [k, label]),
  ['converted', 'Became clients']
];

const TONE = Object.fromEntries(OUTCOMES.map(o => [o[0], [o[2], o[3]]]));

/* What each tab's list is, said once. */
const NOTES = {
  came: 'Everything that arrived on the pipeline in this window.',
  deal: 'They reached Won, so the row left the board for the Deals page.',
  none: 'Marked No response on the board, with what was tried written beside it.',
  dropped: 'They said no. Marked Not interested on the board, with a reason.',
  converted: 'On the books now — matched by the link, or by the address they signed up with.'
};

/** The scope a cut names, and which tab it opens on. */
function scopeOf(cut, r) {
  const [a, b, c] = String(cut).split(':');

  if (a === 'source' || a === 'owner') {
    const hit = (a === 'source' ? r.bySource : r.byOwner).find(x => x.key === b);
    if (!hit) return null;
    return {
      rows: hit.rows,
      title: b,
      sub: a === 'source' ? 'Every lead from this source' : 'Every lead carried by this person',
      tab: c || 'came'
    };
  }

  return { rows: r.total.rows, title: 'All leads', sub: 'Every source, everybody', tab: a || 'came' };
}

export default function LeadDetail({ cut, report, period, onClose }) {
  const { S, crm, rows: clients } = useApp();
  const scope = useMemo(() => scopeOf(cut, report), [cut, report]);
  const [tab, setTab] = useState(scope ? scope.tab : 'came');
  const [q, setQ] = useState('');

  /* Escape closes it, the way the shared sheet shell does. The footer
     button that used to be the other way out has gone - the X is the
     only one on screen, and a window with one way out had better take
     the key everybody presses. */
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  /* Which of the scope's rows each tab holds, counted once so the tab
     can carry its own number and an empty one can be stepped back. */
  const buckets = useMemo(() => {
    if (!scope) return {};
    const inScope = new Set(scope.rows);
    const out = { came: scope.rows };
    OUTCOMES.forEach(([k]) => { out[k] = (report.byOutcome[k] || []).filter(d => inScope.has(d)); });
    out.converted = report.convertedRows.filter(d => inScope.has(d));
    /* Not a tab — only the sum below needs it. See recon. */
    out.open = (report.byOutcome.open || []).filter(d => inScope.has(d));
    return out;
  }, [scope, report]);

  /*
   * The same leads, counted the way the Pipeline page counts them.
   *
   * The two screens answer different questions and both are right: the
   * report asks how many arrived, the board asks how many are still
   * open to work. So "53 today" here against "33 today" there reads as
   * one of them being broken, every time, until somebody works out
   * where the twenty went.
   *
   * So it is shown as the subtraction rather than claimed as a
   * sentence, and only on the tab whose total is the one in question.
   * The three things that take a row off the board are the board's own
   * three rules: it became a deal, somebody closed it off, or they
   * already pay us. What is left is the board.
   */
  const recon = useMemo(() => {
    if (!buckets.came) return null;
    const still = buckets.open || [];
    /*
     * customerOfLead, not clientOfDeal — the board's own rule, and the
     * difference between them is the whole reason for being careful.
     *
     * Somebody who registered on their own is a customer, so the board
     * sweeps them off. An account this very row created by being pushed
     * across is not: pushing a lead to a client and then finding it back
     * on the board is somebody deliberately working it again, and the
     * board keeps it. Counted with clientOfDeal, three such rows came
     * off this sum that never came off the board, and the total read
     * 33 against a Pipeline page saying 36.
     */
    const client = still.filter(d => customerOfLead(clients, d)).length;
    return {
      came: buckets.came.length,
      deal: (buckets.deal || []).length,
      closed: (buckets.none || []).length + (buckets.dropped || []).length,
      client,
      board: still.length - client
    };
  }, [buckets, clients]);

  const lines = useMemo(() => {
    const all = (buckets[tab] || []).map(d => lineOf(S, crm, clients, d));
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(x => [x.name, x.email, x.source, x.owner, x.type, x.client]
      .filter(Boolean).join(' ').toLowerCase().includes(needle));
  }, [buckets, tab, S, crm, clients, q]);

  if (!scope) return null;
  const here = buckets[tab] || [];

  return (
    <div className="ldwrap" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="ldsheet" onClick={e => e.stopPropagation()}>
        <header className="ldhead">
          <div className="ic g-purple"><Icon name="flow" solid /></div>
          <div>
            <h3>{scope.title}</h3>
            <p>{scope.sub} · {period.label}</p>
          </div>
          <button type="button" className="ldx" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
        </header>

        {/* A tab per outcome, each carrying its own count, so switching
            is a decision somebody can make before they click. */}
        <nav className="ldtabs" role="tablist">
          {TABS.map(([k, label]) => {
            const n = (buckets[k] || []).length;
            return (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={tab === k}
                className={`ldtab ${k}${tab === k ? ' on' : ''}${n ? '' : ' nil'}`}
                onClick={() => { setTab(k); setQ(''); }}
                disabled={!n}
              >
                {label}<span>{n}</span>
              </button>
            );
          })}
        </nav>

        {/* See recon — the whole answer to "why does the Pipeline page
            say something else", under the total it is about. */}
        {tab === 'came' && recon && recon.came ? (
          <div className="srrec">
            <p>Why the Pipeline page shows a smaller number for the same days</p>
            <ol>
              <li className="rc"><b>{recon.came.toLocaleString()}</b><span>leads came</span></li>
              <li className="rop" aria-hidden="true">−</li>
              <li className="rc off"><b>{recon.deal.toLocaleString()}</b><span>moved to deals</span></li>
              <li className="rop" aria-hidden="true">−</li>
              <li className="rc off"><b>{recon.closed.toLocaleString()}</b><span>closed off</span></li>
              <li className="rop" aria-hidden="true">−</li>
              <li className="rc off" title="Registered on their own, so the board sweeps them off. Not the same as Became clients, which counts everyone on the books however they got there.">
              <b>{recon.client.toLocaleString()}</b><span>already clients</span>
            </li>
              <li className="rop" aria-hidden="true">=</li>
              <li className="rc end"><b>{recon.board.toLocaleString()}</b><span>still on the pipeline board</span></li>
            </ol>
          </div>
        ) : null}

        <div className="ldtop">
          <b>{here.length}</b>
          <span>lead{here.length === 1 ? '' : 's'}</span>
          <em>{NOTES[tab] || ''}</em>
          <div className="ldfind">
            <Icon name="search" />
            <input value={q} placeholder="Find a lead…" onChange={e => setQ(e.target.value)} />
          </div>
        </div>

        <div className="ldbody">
          {!lines.length ? (
            <EmptyState icon="search" tone="g-slate" title={q ? 'Nothing matches that' : 'No rows on this tab'} />
          ) : (
            <ol className="ldlist">
              {lines.map(x => {
                const key = Object.keys(OUTCOME_LABEL).find(k => OUTCOME_LABEL[k] === x.outcome);
                const [tone, icon] = TONE[key] || ['gr-slate', 'flow'];
                return (
                  <li key={x.id} className="ldrow">
                    <Avatar name={x.name} />
                    <div className="ldwho">
                      <b>{x.name}</b>
                      <small>{x.email || 'no address'}{x.type ? ` · ${x.type}` : ''}</small>
                    </div>
                    <div className="ldtags">
                      <Pill tone="gr-cyan" className="ldp">{x.source}</Pill>
                      <Pill tone={tone} icon={icon} className="ldp">{x.outcome}</Pill>
                      {x.converted === 'Yes'
                        ? <Pill tone="gr-teal" icon="check" className="ldp">{x.client || 'Client'}</Pill>
                        : null}
                    </div>
                    <div className="ldmeta">
                      <span>{x.owner}</span>
                      <small>{x.arrived}</small>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <footer className="ldfoot">
          <span className="dim">{lines.length} shown of {here.length}</span>
        </footer>
      </div>
    </div>
  );
}
